import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FaHospital,
    FaEye,
    FaToggleOn,
    FaToggleOff,
    FaBan,
    FaUserMd,
    FaSearch,
    FaFilter,
    FaPlus,
    FaUpload,
    FaImage,
    FaMapMarkerAlt,
    FaTrash,
} from "react-icons/fa";
import {
    showSuccessToast,
    showErrorToast,
} from "../../../shared/utils/toastHelper";
import { useTitle } from "../../../context/TitleContext";
import EnhancedPagination from "../../../shared/components/EnhancedPagination";
import ConfirmModal from "../../../shared/components/ConfirmModal";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminClinics = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [clinics, setClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // "" = default (no status), active → 1, inactive → 0
    const [filteredClinics, setFilteredClinics] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, clinicId: null, clinicName: '' });
    const [deletingClinic, setDeletingClinic] = useState(null);

    useEffect(() => {
        setTitle("العيادات");
    }, [setTitle]);

    useEffect(() => {
        fetchClinics();
    }, [searchTerm, statusFilter, currentPage]);

    useEffect(() => {
        filterClinics();
    }, [clinics]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem("adminkhadamat");
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                return parsed.data?.token?.access_token || parsed.data?.access_token;
            } catch {
                return null;
            }
        }
        return null;
    };

    const fetchClinics = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            
            if (!token) {
                throw new Error("لا يوجد token للمصادقة");
            }

            // بناء الـ URL مع الفلاتر
            let url = `${BASE_URL}/clinics/get_all`;
            const params = new URLSearchParams();
            
            // فلتر الحالة: الافتراضي بدون status؛ التصفية: ?status=1 أو ?status=0
            if (statusFilter === "active") {
                params.append("status", "1");
            } else if (statusFilter === "inactive") {
                params.append("status", "0");
            }
            
            // إضافة معامل البحث
            if (searchTerm.trim()) {
                params.append("search", searchTerm.trim());
            }
            
            // إضافة معامل الصفحة
            params.append("page", currentPage);
            
            // إضافة المعاملات للـ URL
            if (params.toString()) {
                url += `?${params.toString()}`;
            }

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Accept-Language": "ar",
                    lang: "ar",
                },
            });

            if (
                response.data.status === "success" ||
                response.data.statusCode === 200
            ) {
                setClinics(response.data.result?.data || []);
                setPagination(response.data.result?.pagination || null);
            } else {
                throw new Error(response.data.message || "فشل في جلب العيادات");
            }
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في جلب العيادات"
            );
        } finally {
            setLoading(false);
        }
    };

    const filterClinics = () => {
        // الفلترة تتم من الـ API، لذلك نعرض جميع النتائج
        setFilteredClinics(clinics);
    };

    const handleStatusChange = async (clinicId, currentStatus) => {
        try {
            const token = getAuthToken();
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            const newStatus = currentStatus === "1" ? "0" : "1";

            await axios.post(
                `${BASE_URL}/clinics/change_status/${clinicId}`,
                {
                    status: newStatus,
                },
                {
                headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                        "Content-Type": "application/json",
                    },
                }
            );

            showSuccessToast("تم تغيير حالة العيادة بنجاح");
            fetchClinics(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast("فشل في تغيير حالة العيادة");
        }
    };

    const handleBanChange = async (clinicId, currentBanStatus) => {
        try {
            const token = getAuthToken();
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            const newBanStatus = currentBanStatus === "1" ? "0" : "1";

            await axios.post(
                `${BASE_URL}/clinics/change_ban/${clinicId}?status=${newBanStatus}`,
                {},
                {
                headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                    },
                }
            );

            showSuccessToast("تم تغيير حالة الحظر بنجاح");
            fetchClinics(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast("فشل في تغيير حالة الحظر");
        }
    };

    const handleDeleteClick = (clinicId, clinicName) => {
        setConfirmModal({
            isOpen: true,
            clinicId: clinicId,
            clinicName: clinicName || 'هذه العيادة'
        });
    };

    const handleDeleteClinic = async () => {
        if (!confirmModal.clinicId) return;

        try {
            setDeletingClinic(confirmModal.clinicId);
            const token = getAuthToken();
            
            if (!token) {
                showErrorToast("لا يوجد token للمصادقة");
                return;
            }

            await axios.delete(
                `${BASE_URL}/clinics/delete/${confirmModal.clinicId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Accept-Language": "ar",
                        lang: "ar",
                    },
                }
            );

            showSuccessToast("تم حذف العيادة بنجاح");
            setConfirmModal({ isOpen: false, clinicId: null, clinicName: '' });
            fetchClinics(); // إعادة تحميل القائمة
        } catch (error) {
            showErrorToast(
                error.response?.data?.message || error.message || "فشل في حذف العيادة"
            );
        } finally {
            setDeletingClinic(null);
        }
    };


    return (
        <div className="main_content">
            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0" style={{ fontSize: "20px" }}>
                        <FaHospital className="mx-2" style={{ color: "#184194" }} />
                        العيادات
                    </h2>
                    <button
                        className="btn text-white"
                        style={{ backgroundColor: "rgb(24, 65, 148)" }}
                        onClick={() => navigate('/admin-clinics/add')}
                    >
                        <FaPlus className="mx-2" />
                        إضافة عيادة
                    </button>
                </div>

                {/* Search and Filter */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label">الحالة</label>
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="">الكل (الافتراضي)</option>
                                    <option value="active">نشط</option>
                                    <option value="inactive">غير نشط</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Clinics Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل العيادات...</p>
                            </div>
                        ) : filteredClinics.length === 0 ? (
                            <div className="text-center py-5">
                                <FaHospital
                                    className="text-muted"
                                    style={{ fontSize: "3rem" }}
                                />
                                <p className="mt-3 text-muted">
                                    {searchTerm ? "لا توجد نتائج للبحث" : "لا توجد عيادات"}
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-modern">
                                    <thead>
                                        <tr>
                                            <th className="text-center">#</th>
                                            <th className="text-center">اسم العيادة</th>
                                            <th className="text-center">العنوان</th>
                                            <th className="text-center">الهاتف</th>
                                            <th className="text-center">الحالة</th>
                                            <th className="text-center">الحظر</th>
                                            <th className="text-center">الإجراءات</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredClinics.map((clinic, index) => (
                                            <tr key={clinic.id} className="table-row-modern">
                                                <td className="text-center">
                                                    <div className="order-number-badge">
                                                        #
                                                        {(pagination?.current_page - 1) *
                                                            (pagination?.per_page || 10) +
                                                            index +
                                                            1}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="clinic-name">
                                                        {clinic.title || "عيادة بدون اسم"}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="address-wrap text-wrap">
                                                        {clinic.address || "لا يوجد عنوان"}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <div className="phone-badge">
                                                        {clinic.phone
                                                            ? (() => {
                                                            let phoneStr = clinic.phone.toString().trim();
                                                                phoneStr = phoneStr.replace(/\s+/g, "");
                                                            
                                                            // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
                                                                if (phoneStr.startsWith("+")) {
                                                                    return phoneStr.slice(1) + "+";
                                                            }
                                                            
                                                            // إذا كان الرقم لا يبدأ بـ + - نضيف + في النهاية
                                                                return phoneStr + "+";
                                                            })()
                                                            : "لا يوجد هاتف"}
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    {clinic.status === "1" ? (
                                                        <span className="badge bg-success text-white">
                                                            نشط
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-danger text-white">
                                                            غير نشط
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {clinic.ban === "1" ? (
                                                        <span className="badge bg-danger text-white">
                                                            محظور
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-success text-white">
                                                            غير محظور
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <button
                                                            className="btn btn-sm btn-doctors"
                                                            title="عرض الأطباء"
                                                            onClick={() =>
                                                                navigate(`/admin-clinics/doctors/${clinic.id}`)
                                                            }
                                                        >
                                                            <FaUserMd className="mx-1" />
                                                            الأطباء
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-toggle-small"
                                                            title={
                                                                clinic.status === "1"
                                                                    ? "إلغاء التفعيل"
                                                                    : "تفعيل"
                                                            }
                                                            onClick={() =>
                                                                handleStatusChange(clinic.id, clinic.status)
                                                            }
                                                        >
                                                            {clinic.status === "1" ? (
                                                                <FaToggleOff />
                                                            ) : (
                                                                <FaToggleOn />
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-ban"
                                                            title={clinic.ban === "1" ? "إلغاء الحظر" : "حظر"}
                                                            onClick={() =>
                                                                handleBanChange(clinic.id, clinic.ban)
                                                            }
                                                        >
                                                            <FaBan />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* Pagination */}
                        {pagination && (
                            <div className="mt-4">
                                <EnhancedPagination
                                    currentPage={pagination.current_page || currentPage}
                                    totalPages={pagination.pages_count || 1}
                                    onPageChange={setCurrentPage}
                                    totalItems={pagination.total || 0}
                                    itemsPerPage={pagination.per_page || 10}
                                    showInfo={true}
                                    className="mt-4"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirm Delete Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, clinicId: null, clinicName: '' })}
                onConfirm={handleDeleteClinic}
                title="تأكيد الحذف"
                message={`هل أنت متأكد من حذف ${confirmModal.clinicName}؟`}
                confirmText="حذف"
                cancelText="إلغاء"
                confirmButtonColor="#dc3545"
                type="danger"
            />
        </div>
    );
};

export default AdminClinics;

// CSS لتحسين عرض العنوان
const styles = `
    .address-display {
        background: #f8fafc !important;
        color: #64748b !important;
        padding: 0.5rem 1rem !important;
        border-radius: 8px !important;
        font-size: 0.875rem !important;
        border: 1px solid #e2e8f0 !important;
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: unset !important;
        display: inline-block !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
        line-height: 1.4 !important;
        max-width: none !important;
        min-width: 300px !important;
        width: auto !important;
        max-width: 500px !important;
    }

    .address-wrap {
        background: #f8fafc !important;
        color: #64748b !important;
        padding: 0.5rem 1rem !important;
        border-radius: 8px !important;
        font-size: 0.875rem !important;
        border: 1px solid #e2e8f0 !important;
        white-space: normal !important;
        word-wrap: break-word !important;
        word-break: break-word !important;
        overflow: visible !important;
        display: inline-block !important;
        text-align: center !important;
        line-height: 1.4 !important;
        max-width: none !important;
        min-width: 300px !important;
        width: auto !important;
        max-width: 500px !important;
    }

    .clinic-name {
        font-weight: 500 !important;
        color: #374151 !important;
        white-space: nowrap !important;
        overflow: visible !important;
        text-overflow: unset !important;
        max-width: none !important;
        min-width: 200px !important;
        width: auto !important;
    }

    /* إجبار خلية العنوان على التمدد */
    .table-modern td:has(.address-display),
    .table-modern td:has(.address-wrap) {
        width: auto !important;
        min-width: 400px !important;
        max-width: 500px !important;
        overflow: visible !important;
        white-space: normal !important;
    }

    .table-modern td:has(.clinic-name) {
        width: auto !important;
        min-width: 250px !important;
        max-width: 350px !important;
    }

    .btn-toggle-small {
        padding: 0.3rem 0.5rem !important;
        font-size: 0.75rem !important;
        min-width: 35px !important;
        width: auto !important;
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        color: white !important;
    }
    
    .btn-toggle-small svg {
        color: white !important;
        width: 14px !important;
        height: 14px !important;
    }
    
    .btn-info {
        padding: 0.3rem 0.5rem !important;
        font-size: 0.75rem !important;
    }
    
    .btn-ban {
        padding: 0.25rem 0.4rem !important;
        font-size: 0.7rem !important;
    }
    
    .btn-info svg {
        width: 14px !important;
        height: 14px !important;
    }
    
    .btn-ban svg {
        width: 12px !important;
        height: 12px !important;
    }
    
    .btn-doctors {
        padding: 0.2rem 0.4rem !important;
        font-size: 0.7rem !important;
        background-color: rgb(24, 65, 148) !important;
        color: white !important;
        border: none !important;
    }
    
    .btn-doctors svg {
        width: 12px !important;
        height: 12px !important;
        color: white !important;
    }
`;

// إضافة الـ CSS للصفحة
if (typeof document !== "undefined") {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}
